/**
 * ResumeAuditScreen.jsx — Premium AI Resume Audit Dashboard with Diff & Apply
 */
import React, { useState } from 'react';
import {
  Star, ClipboardList, TrendingUp, AlertCircle,
  Loader, CheckCircle, Info, Sparkles
} from 'lucide-react';

/* ── Section progress bar ── */
function SectionRow({ label, score, max, color }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{score}<span style={{ fontSize: 9, color: 'var(--text-muted)', fontWeight: 400 }}>/{max}</span></span>
      </div>
      <div style={{ height: 6, backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
}

export default function ResumeAuditScreen({
  auditFull,
  auditPreview,
  auditLoading,
  auditError,
  resumeData,
  theme = 'dark',
  authToken,
  onRunAudit,
  onCreditsUpdate,
  onUpdateResumeData // trigger resume reload on the parent
}) {
  const T = theme === 'dark';
  const data = auditFull || auditPreview;

  // Local interactive states for AI Fixes
  const [explanations, setExplanations] = useState({}); // issueId -> string
  const [explainLoadingId, setExplainLoadingId] = useState(null);
  const [proposedFixes, setProposedFixes] = useState({}); // issueId -> { before, after, path }
  const [fixingId, setFixingId] = useState(null);
  const [appliedIds, setAppliedIds] = useState([]);
  const [applyLoadingId, setApplyLoadingId] = useState(null);

  const gradeColor = !data
    ? 'var(--brand)'
    : data.grade?.startsWith('A') ? '#059669'
    : data.grade?.startsWith('B') ? '#0369a1'
    : data.grade?.startsWith('C') ? '#b45309'
    : '#ef4444';

  const handleExplain = async (item) => {
    if (explainLoadingId) return;
    setExplainLoadingId(item.id);
    try {
      const res = await fetch("/api/audit/explain-issue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          issue: item.issue,
          section: item.section
        })
      });
      const resData = await res.json();
      if (res.ok && resData.explanation) {
        setExplanations(prev => ({ ...prev, [item.id]: resData.explanation }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setExplainLoadingId(null);
    }
  };

  const handleFix = async (item) => {
    if (fixingId) return;
    setFixingId(item.id);
    try {
      const res = await fetch("/api/audit/fix-issue", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          id: item.id,
          issue: item.issue,
          section: item.section,
          fix: item.fix
        })
      });
      const resData = await res.json();
      if (!res.ok) {
        alert(resData.error || "Failed to generate AI fix.");
        return;
      }
      setProposedFixes(prev => ({
        ...prev,
        [item.id]: {
          before: resData.before,
          after: resData.after,
          path: resData.path
        }
      }));
      if (onCreditsUpdate && typeof resData.credits_remaining === 'number') {
        onCreditsUpdate(resData.credits_remaining);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFixingId(null);
    }
  };

  const handleApplyFix = async (item) => {
    const proposed = proposedFixes[item.id];
    if (!proposed || applyLoadingId) return;
    setApplyLoadingId(item.id);
    try {
      const res = await fetch("/api/audit/apply-fix", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          path: proposed.path,
          value: proposed.after
        })
      });
      if (res.ok) {
        setAppliedIds(prev => [...prev, item.id]);
        if (onUpdateResumeData) {
          onUpdateResumeData(); // reload resume on preview canvas
        }
      } else {
        alert("Failed to apply fix.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setApplyLoadingId(null);
    }
  };

  return (
    <div style={{
      height: '100%', overflowY: 'auto',
      padding: '20px', fontFamily: "'Inter', sans-serif",
      background: T ? '#0b0b14' : '#F8FAFC',
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 11,
          background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Star size={16} color={T ? '#c4b5fd' : '#7c3aed'} strokeWidth={1.8} />
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
            AI Resume Audit & Fixer
          </h2>
          <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Comprehensive report & direct AI optimization
          </p>
        </div>
      </div>

      {/* Grade + Score card */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
        borderRadius: 14, padding: 20, marginBottom: 14,
      }}>
        {data ? (
          <>
            {/* Grade row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                background: `${gradeColor}18`, border: `2px solid ${gradeColor}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 900, color: gradeColor,
              }}>
                {data.grade}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                  {data.grade_label}
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {data.critical_count > 0 && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                      background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
                      {data.critical_count} critical
                    </span>
                  )}
                  {data.warning_count > 0 && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                      background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)' }}>
                      {data.warning_count} warnings
                    </span>
                  )}
                  {data.passed_count > 0 && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                      background: 'rgba(5,150,105,0.1)', color: '#059669', border: '1px solid rgba(5,150,105,0.2)' }}>
                      {data.passed_count} passed
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: gradeColor, lineHeight: 1 }}>
                  {data.overall_score}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>/100</div>
              </div>
            </div>

            {/* Section progress */}
            {auditFull && [
              { key: 'content_quality',   label: 'Content Quality',   max: 30, color: '#059669' },
              { key: 'ats_compatibility', label: 'ATS Compatibility',  max: 25, color: '#0369a1' },
              { key: 'structure_layout',  label: 'Structure & Layout', max: 20, color: '#7c3aed' },
              { key: 'completeness',      label: 'Completeness',       max: 15, color: '#b45309' },
              { key: 'readability',       label: 'Readability',        max: 10, color: '#0891b2' },
            ].map(row => (
              <SectionRow
                key={row.key}
                label={row.label}
                score={auditFull.sections?.[row.key]?.score ?? 0}
                max={row.max}
                color={row.color}
              />
            ))}

            {/* Score projection */}
            {data.estimated_score_after_fixes > data.overall_score && (
              <div style={{
                marginTop: 14, padding: '10px 12px', borderRadius: 10,
                background: 'rgba(5,150,105,0.07)', border: '1px solid rgba(5,150,105,0.2)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <TrendingUp size={14} color="#059669" />
                <p style={{ fontSize: 11, color: '#059669', margin: 0, fontWeight: 600 }}>
                  Fix issues → score jumps to{' '}
                  <strong>{data.estimated_score_after_fixes}/100</strong>
                </p>
              </div>
            )}
          </>
        ) : (
          /* Empty state */
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <ClipboardList size={36} color="var(--text-muted)" strokeWidth={1.2} style={{ marginBottom: 10 }} />
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
              {resumeData?.name ? 'Ready for Audit' : 'Build Resume First'}
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
              {resumeData?.name
                ? 'Get a comprehensive grade across 5 dimensions with instant AI fixes'
                : 'Complete your resume in AI Chat, then run an audit'}
            </p>
          </div>
        )}

        {/* Action plan list */}
        {auditFull?.action_plan?.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
              color: 'var(--text-muted)', margin: '0 0 12px' }}>
              Action Plan & AI Fixes
            </p>
            {auditFull.action_plan.slice(0, 8).map((item, i) => {
              const isApplied = appliedIds.includes(item.id);
              const hasProposed = !!proposedFixes[item.id];
              const explanation = explanations[item.id];

              return (
                <div key={item.id || i} style={{
                  padding: '12px 14px', marginBottom: 10, borderRadius: 10,
                  background: T ? 'rgba(255,255,255,0.02)' : '#fff',
                  border: '1px solid var(--card-border)',
                }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      background: 'var(--brand-light)', border: '1px solid var(--brand-border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800, color: 'var(--brand-text)',
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>
                        {item.issue}
                      </p>
                      <p style={{ fontSize: 10, color: 'var(--text-muted)', margin: 0 }}>
                        {item.impact} · Effort: {item.effort}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: 6 }}>
                      {/* Explain Button */}
                      {!isApplied && (
                        <button
                          onClick={() => handleExplain(item)}
                          disabled={explainLoadingId === item.id}
                          style={{
                            fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                            background: 'none', border: '1px solid var(--card-border)',
                            color: 'var(--text-secondary)', cursor: 'pointer'
                          }}
                        >
                          {explainLoadingId === item.id ? <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} /> : <Info size={10} />}
                        </button>
                      )}

                      {/* Fix Button */}
                      {item.ai_fix && !isApplied && !hasProposed && (
                        <button
                          onClick={() => handleFix(item)}
                          disabled={fixingId !== null}
                          style={{
                            fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                            background: 'var(--brand-light)', border: '1px solid var(--brand-border)',
                            color: 'var(--brand-text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                          }}
                        >
                          {fixingId === item.id ? (
                            <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <><Sparkles size={10} /> Fix (1cr)</>
                          )}
                        </button>
                      )}

                      {isApplied && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#059669', fontWeight: 700 }}>
                          <CheckCircle size={12} /> Applied
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Inline Explanation Drawer */}
                  {explanation && (
                    <div style={{
                      marginTop: 10, padding: '8px 10px', borderRadius: 6,
                      backgroundColor: 'rgba(3,105,161,0.06)', borderLeft: '3px solid #0369a1',
                      fontSize: 10, color: 'var(--text-secondary)'
                    }}>
                      {explanation}
                    </div>
                  )}

                  {/* Proposed Fix Comparisons */}
                  {hasProposed && !isApplied && (
                    <div style={{ marginTop: 12, borderTop: '1px dashed var(--card-border)', paddingTop: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                        <div style={{ padding: 8, background: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.1)', borderRadius: 6 }}>
                          <strong style={{ fontSize: 9, color: '#EF4444', display: 'block', marginBottom: 2 }}>ORIGINAL:</strong>
                          <p style={{ fontSize: 10, margin: 0, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{proposedFixes[item.id].before}</p>
                        </div>
                        <div style={{ padding: 8, background: 'rgba(5,150,105,0.03)', border: '1px solid rgba(5,150,105,0.1)', borderRadius: 6 }}>
                          <strong style={{ fontSize: 9, color: '#059669', display: 'block', marginBottom: 2 }}>PROPOSED FIX:</strong>
                          <p style={{ fontSize: 10, margin: 0, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{proposedFixes[item.id].after}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setProposedFixes(prev => {
                            const copy = { ...prev };
                            delete copy[item.id];
                            return copy;
                          })}
                          style={{
                            fontSize: 10, padding: '4px 10px', border: '1px solid var(--card-border)',
                            borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--text-secondary)'
                          }}
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => handleApplyFix(item)}
                          disabled={applyLoadingId === item.id}
                          style={{
                            fontSize: 10, fontWeight: 700, padding: '4px 12px', border: 'none',
                            borderRadius: 6, background: '#059669', color: '#fff', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4
                          }}
                        >
                          {applyLoadingId === item.id ? (
                            <Loader size={10} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            "Apply Fix"
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Error message */}
        {auditError && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 12 }}>
            <AlertCircle size={12} color="var(--status-danger)" />
            <p style={{ fontSize: 11, color: 'var(--status-danger)', margin: 0 }}>{auditError}</p>
          </div>
        )}

        {/* Run audit button */}
        {!auditFull && (
          <button
            className="btn-primary"
            onClick={onRunAudit}
            disabled={auditLoading || !resumeData?.name}
            style={{ width: '100%', marginTop: 16, fontSize: 13, padding: '12px' }}
          >
            {auditLoading
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Analyzing...</>
              : auditPreview
              ? <><Star size={14} /> Get Full Audit Report (2 credits)</>
              : <><Star size={14} /> Run AI Resume Audit (2 credits)</>
            }
          </button>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
